import React, { useState } from "react";
import {
  Table,
  Card,
  Select,
  DatePicker,
  Button,
  Tag,
  Space,
  Dropdown,
  Modal,
  InputNumber,
  message,
  Tooltip,
} from "antd";
import {
  EyeOutlined,
  MailOutlined,
  CheckCircleOutlined,
  DeleteOutlined,
  MoreOutlined,
  FileTextOutlined,
  FilterOutlined,
  ReloadOutlined,
  StopOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import type { ColumnsType } from "antd/es/table";
import {
  useStorageInvoices,
  useDeleteStorageInvoice,
  useMarkStorageInvoiceAsSent,
  useMarkStorageInvoiceAsPaid,
  useUpdateStorageInvoice,
} from "~/services/storage/storage.queries";
import {
  StorageInvoice,
  StorageInvoiceStatus,
} from "~/services/storage/storage.api";
import { useGetClients } from "~/services/client";
import { RateType } from "~/utils/constants";
import { getClientName } from "~/utils";

interface StorageBillingHistoryProps {
  onViewInvoice?: (invoice: StorageInvoice) => void;
}

export const StorageBillingHistory: React.FC<StorageBillingHistoryProps> = ({
  onViewInvoice,
}) => {
  // Filters
  const [clientFilter, setClientFilter] = useState<string | undefined>();
  const [yearFilter, setYearFilter] = useState<number | undefined>(
    dayjs().year()
  );
  const [monthFilter, setMonthFilter] = useState<number | undefined>();
  const [statusFilter, setStatusFilter] = useState<
    StorageInvoiceStatus | undefined
  >();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Mark as paid modal
  const [paidModalVisible, setPaidModalVisible] = useState(false);
  const [selectedInvoiceForPaid, setSelectedInvoiceForPaid] =
    useState<StorageInvoice | null>(null);
  const [paidAmount, setPaidAmount] = useState<number>(0);
  const [paidDate, setPaidDate] = useState<dayjs.Dayjs>(dayjs());

  // Delete confirmation modal
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<StorageInvoice | null>(null);

  // Cancel/void confirmation modal
  const [cancelModalVisible, setCancelModalVisible] = useState(false);
  const [invoiceToCancel, setInvoiceToCancel] = useState<StorageInvoice | null>(null);

  // Fetch invoices
  const {
    data: invoicesData,
    isLoading,
    refetch,
  } = useStorageInvoices({
    clientId: clientFilter,
    year: yearFilter,
    month: monthFilter,
    status: statusFilter,
    page,
    limit: pageSize,
  });

  // Fetch clients for filter dropdown
  const { data: clientsData } = useGetClients({
    params: { rateType: RateType.STORAGE_CLIENT, pageSize: 200 },
  });

  // Mutations
  const deleteInvoice = useDeleteStorageInvoice();
  const markAsSent = useMarkStorageInvoiceAsSent();
  const markAsPaid = useMarkStorageInvoiceAsPaid();
  const updateInvoice = useUpdateStorageInvoice();

  const handleDelete = (invoice: StorageInvoice) => {
    setInvoiceToDelete(invoice);
    setDeleteModalVisible(true);
  };

  const confirmDelete = async () => {
    if (!invoiceToDelete) return;

    try {
      await deleteInvoice.mutateAsync(invoiceToDelete.id);
      message.success("Invoice deleted successfully");
      setDeleteModalVisible(false);
      setInvoiceToDelete(null);
    } catch {
      message.error("Failed to delete invoice");
    }
  };

  const handleCancel = (invoice: StorageInvoice) => {
    setInvoiceToCancel(invoice);
    setCancelModalVisible(true);
  };

  const confirmCancel = async () => {
    if (!invoiceToCancel) return;

    try {
      await updateInvoice.mutateAsync({
        id: invoiceToCancel.id,
        updates: { status: StorageInvoiceStatus.CANCELLED },
      });
      message.success("Invoice voided successfully");
      setCancelModalVisible(false);
      setInvoiceToCancel(null);
    } catch {
      message.error("Failed to void invoice");
    }
  };

  const handleMarkAsSent = async (invoice: StorageInvoice) => {
    try {
      await markAsSent.mutateAsync(invoice.id);
      message.success("Invoice marked as sent");
    } catch {
      message.error("Failed to mark invoice as sent");
    }
  };

  const openPaidModal = (invoice: StorageInvoice) => {
    setSelectedInvoiceForPaid(invoice);
    setPaidAmount(Number(invoice.totalAmount) || 0);
    setPaidDate(dayjs());
    setPaidModalVisible(true);
  };

  const handleMarkAsPaid = async () => {
    if (!selectedInvoiceForPaid) return;

    try {
      await markAsPaid.mutateAsync({
        id: selectedInvoiceForPaid.id,
        paidAmount,
        paidDate: paidDate.toISOString(),
      });
      message.success("Invoice marked as paid");
      setPaidModalVisible(false);
      setSelectedInvoiceForPaid(null);
    } catch {
      message.error("Failed to mark invoice as paid");
    }
  };

  const getStatusTag = (status: StorageInvoiceStatus) => {
    const config: Record<
      StorageInvoiceStatus,
      { color: string; label: string }
    > = {
      [StorageInvoiceStatus.DRAFT]: { color: "default", label: "Draft" },
      [StorageInvoiceStatus.SENT]: { color: "blue", label: "Sent" },
      [StorageInvoiceStatus.PAID]: { color: "green", label: "Paid" },
      [StorageInvoiceStatus.CANCELLED]: { color: "red", label: "Cancelled" },
    };
    const { color, label } = config[status];
    return <Tag color={color}>{label}</Tag>;
  };

  const getActionItems = (invoice: StorageInvoice) => {
    const items = [];

    if (onViewInvoice) {
      items.push({
        key: "view",
        label: "View / Print",
        icon: <EyeOutlined />,
        onClick: () => onViewInvoice(invoice),
      });
    }

    if (invoice.status === StorageInvoiceStatus.DRAFT) {
      items.push({
        key: "send",
        label: "Mark as Sent",
        icon: <MailOutlined />,
        onClick: () => handleMarkAsSent(invoice),
      });
    }

    if (
      invoice.status === StorageInvoiceStatus.DRAFT ||
      invoice.status === StorageInvoiceStatus.SENT
    ) {
      items.push({
        key: "paid",
        label: "Mark as Paid",
        icon: <CheckCircleOutlined />,
        onClick: () => openPaidModal(invoice),
      });

      // Void/Cancel option for draft or sent invoices
      items.push({
        key: "cancel",
        label: "Void Invoice",
        icon: <StopOutlined />,
        danger: true,
        onClick: () => handleCancel(invoice),
      });
    }

    if (invoice.status === StorageInvoiceStatus.DRAFT) {
      items.push({
        key: "delete",
        label: "Delete",
        icon: <DeleteOutlined />,
        danger: true,
        onClick: () => handleDelete(invoice),
      });
    }

    return items;
  };

  const columns: ColumnsType<StorageInvoice> = [
    {
      title: "Invoice #",
      dataIndex: "invoiceNumber",
      key: "invoiceNumber",
      width: 160,
      render: (text: string, record: StorageInvoice) => (
        <a
          onClick={() => onViewInvoice?.(record)}
          className="text-blue-600 hover:text-blue-800"
        >
          {text}
        </a>
      ),
    },
    {
      title: "Client",
      key: "client",
      width: 200,
      render: (_: unknown, record: StorageInvoice) =>
        record.client?.companyName ||
        (record.client
          ? getClientName(record.client as any)
          : "Unknown Client"),
    },
    {
      title: "Billing Period",
      key: "billingPeriod",
      width: 130,
      render: (_: unknown, record: StorageInvoice) =>
        dayjs()
          .month(record.billingPeriodMonth - 1)
          .year(record.billingPeriodYear)
          .format("MMM YYYY"),
    },
    {
      title: "Invoice Date",
      dataIndex: "invoiceDate",
      key: "invoiceDate",
      width: 110,
      render: (date: string) => dayjs(date).format("MM/DD/YYYY"),
    },
    {
      title: "Storage",
      dataIndex: "storageTotal",
      key: "storageTotal",
      width: 100,
      align: "right",
      render: (value: number | string) => `$${Number(value || 0).toFixed(2)}`,
    },
    {
      title: "Insurance",
      dataIndex: "insuranceTotal",
      key: "insuranceTotal",
      width: 100,
      align: "right",
      render: (value: number | string) => {
        const num = Number(value || 0);
        return num > 0 ? `$${num.toFixed(2)}` : "-";
      },
    },
    {
      title: "Total",
      dataIndex: "totalAmount",
      key: "totalAmount",
      width: 110,
      align: "right",
      render: (value: number | string) => (
        <span className="font-medium">${Number(value || 0).toFixed(2)}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 100,
      render: (status: StorageInvoiceStatus) => getStatusTag(status),
    },
    {
      title: "",
      key: "actions",
      width: 50,
      render: (_: unknown, record: StorageInvoice) => {
        const items = getActionItems(record);
        if (items.length === 0) return null;

        return (
          <Dropdown menu={{ items }} trigger={["click"]}>
            <Button type="text" icon={<MoreOutlined />} size="small" />
          </Dropdown>
        );
      },
    },
  ];

  const clearFilters = () => {
    setClientFilter(undefined);
    setYearFilter(dayjs().year());
    setMonthFilter(undefined);
    setStatusFilter(undefined);
    setPage(1);
  };

  return (
    <>
      <Card
        title={
          <div className="flex items-center gap-2">
            <FileTextOutlined />
            <span>Billing History</span>
          </div>
        }
        extra={
          <Space>
            <Tooltip title="Refresh">
              <Button
                icon={<ReloadOutlined />}
                onClick={() => refetch()}
                loading={isLoading}
              />
            </Tooltip>
          </Space>
        }
        className="mt-6"
      >
        {/* Filters */}
        <div className="flex flex-wrap gap-3 mb-4">
          <Select
            placeholder="All Clients"
            value={clientFilter}
            onChange={(value) => {
              setClientFilter(value);
              setPage(1);
            }}
            allowClear
            showSearch
            optionFilterProp="children"
            style={{ width: 200 }}
          >
            {clientsData?.items.map((client) => (
              <Select.Option key={client.id} value={client.id}>
                {client.companyName || getClientName(client)}
              </Select.Option>
            ))}
          </Select>

          <Select
            placeholder="Year"
            value={yearFilter}
            onChange={(value) => {
              setYearFilter(value);
              setPage(1);
            }}
            allowClear
            style={{ width: 100 }}
          >
            {[...Array(5)].map((_, i) => {
              const year = dayjs().year() - i;
              return (
                <Select.Option key={year} value={year}>
                  {year}
                </Select.Option>
              );
            })}
          </Select>

          <Select
            placeholder="Month"
            value={monthFilter}
            onChange={(value) => {
              setMonthFilter(value);
              setPage(1);
            }}
            allowClear
            style={{ width: 120 }}
          >
            {[...Array(12)].map((_, i) => (
              <Select.Option key={i + 1} value={i + 1}>
                {dayjs().month(i).format("MMMM")}
              </Select.Option>
            ))}
          </Select>

          <Select
            placeholder="Status"
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              setPage(1);
            }}
            allowClear
            style={{ width: 120 }}
          >
            <Select.Option value={StorageInvoiceStatus.DRAFT}>
              Draft
            </Select.Option>
            <Select.Option value={StorageInvoiceStatus.SENT}>
              Sent
            </Select.Option>
            <Select.Option value={StorageInvoiceStatus.PAID}>
              Paid
            </Select.Option>
            <Select.Option value={StorageInvoiceStatus.CANCELLED}>
              Cancelled
            </Select.Option>
          </Select>

          <Button icon={<FilterOutlined />} onClick={clearFilters}>
            Clear
          </Button>
        </div>

        {/* Invoices Table */}
        <Table
          columns={columns}
          dataSource={invoicesData?.invoices || []}
          rowKey="id"
          loading={isLoading}
          size="small"
          pagination={{
            current: page,
            pageSize,
            total: invoicesData?.total || 0,
            showSizeChanger: true,
            showTotal: (total) => `${total} invoices`,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </Card>

      {/* Mark as Paid Modal */}
      <Modal
        title="Mark Invoice as Paid"
        open={paidModalVisible}
        onCancel={() => {
          setPaidModalVisible(false);
          setSelectedInvoiceForPaid(null);
        }}
        onOk={handleMarkAsPaid}
        okText="Confirm Payment"
        confirmLoading={markAsPaid.isPending}
      >
        {selectedInvoiceForPaid && (
          <div className="space-y-4">
            <div className="p-3 bg-gray-50 rounded">
              <div className="text-sm text-gray-500">Invoice</div>
              <div className="font-medium">
                {selectedInvoiceForPaid.invoiceNumber}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount Paid
              </label>
              <InputNumber
                className="w-full"
                value={paidAmount}
                onChange={(v) => setPaidAmount(v || 0)}
                min={0}
                precision={2}
                addonBefore="$"
                formatter={(value) =>
                  `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                }
                parser={(value) =>
                  Number(value?.replace(/\$\s?|(,*)/g, "")) || 0
                }
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Date
              </label>
              <DatePicker
                className="w-full"
                value={paidDate}
                onChange={(date) => date && setPaidDate(date)}
                format="MM/DD/YYYY"
              />
            </div>
          </div>
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        title="Delete Invoice"
        open={deleteModalVisible}
        onCancel={() => {
          setDeleteModalVisible(false);
          setInvoiceToDelete(null);
        }}
        onOk={confirmDelete}
        okText="Delete"
        okType="danger"
        confirmLoading={deleteInvoice.isPending}
      >
        {invoiceToDelete && (
          <p>
            Are you sure you want to delete invoice{" "}
            <strong>{invoiceToDelete.invoiceNumber}</strong>?
          </p>
        )}
      </Modal>

      {/* Void/Cancel Confirmation Modal */}
      <Modal
        title="Void Invoice"
        open={cancelModalVisible}
        onCancel={() => {
          setCancelModalVisible(false);
          setInvoiceToCancel(null);
        }}
        onOk={confirmCancel}
        okText="Void Invoice"
        okType="danger"
        confirmLoading={updateInvoice.isPending}
      >
        {invoiceToCancel && (
          <div className="space-y-2">
            <p>
              Are you sure you want to void invoice{" "}
              <strong>{invoiceToCancel.invoiceNumber}</strong>?
            </p>
            <p className="text-gray-500 text-sm">
              This will mark the invoice as cancelled. This action cannot be undone.
            </p>
          </div>
        )}
      </Modal>
    </>
  );
};
